import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSEO } from '../hooks/useSEO';
import { addMonthsIso, buildPageJsonLd, formatEditorialDate } from '../lib/site';
import { getGuidePage, getGuideRelated } from '../lib/guideArchitecture';
import DoutoFeatureCard from './DoutoFeatureCard';
import { GuideCalcInvite, GuideDoutoNextStep } from './GuideCtas';
import { GUIDE_DOUTO_PATHS } from '../lib/doutoHandoff';
import DoutoBanner from './DoutoBanner';
import GuiaBanner from './GuiaBanner';
import ContactEmail from './ContactEmail';
import { doutoBannersEnabled } from '../lib/flags';

// Guias onde o banner "guia-fim" substitui os outros convites ao Douto (um só por vez).
const GUIA_FIM_PATHS = ['/interpretacao-dos-resultados', '/diagnostica-alzheimer', '/mini-mental-ou-moca', '/acompanhamento-e-queda-de-pontos'];

/**
 * Índice "Nesta página" (desktop ≥ 1200 px, coluna lateral): lido dos h2 do artigo depois de montar,
 * destaca a seção em leitura. Fora do conteúdo principal e da impressão; não aparece com menos de 3 seções.
 */
function GuideToc() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);
  useEffect(() => {
    const heads = Array.from(document.querySelectorAll('.public-article > div.space-y-12 h2'));
    heads.forEach((h, i) => { if (!h.id) h.id = `secao-${i + 1}`; h.style.scrollMarginTop = '88px'; });
    setItems(heads.map((h) => ({ id: h.id, text: h.textContent.trim() })));
    if (typeof IntersectionObserver === 'undefined' || !heads.length) return undefined;
    const io = new IntersectionObserver((entries) => {
      const vis = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
      if (vis[0]) setActive(vis[0].target.id);
    }, { rootMargin: '-80px 0px -70% 0px' });
    heads.forEach((h) => io.observe(h));
    return () => io.disconnect();
  }, []);
  if (items.length < 3) return null;
  return (
    <nav aria-label="Nesta página" className="mb-6 border-l border-[var(--line)] pl-4" data-guide-toc>
      <p className="text-[length:var(--fs-13)] font-semibold text-[var(--ink-2)]">Nesta página</p>
      <ol className="mt-2 space-y-0.5">
        {items.map((it) => (
          <li key={it.id}>
            <a href={`#${it.id}`} aria-current={active === it.id ? 'location' : undefined} className={`-ml-[17px] block border-l-2 py-1 pl-[15px] text-[length:var(--fs-14)] leading-snug no-underline ${active === it.id ? 'border-[var(--accent)] font-medium text-[var(--ink)]' : 'border-transparent text-[var(--ink-2)] hover:text-[var(--ink)]'}`}>{it.text}</a>
          </li>
        ))}
      </ol>
    </nav>
  );
}

export default function ArticleLayout({ page, answer, children, related = [], references, template = 'guide', noindex = false }) {
  useSEO({ title: page.title, description: page.description, path: page.path, noindex });
  // Nas páginas com o link de texto para o Douto, o cartão grande (inline e lateral) não aparece:
  // uma CTA leve só, sem competir com o conteúdo. Páginas institucionais também não têm cartão.
  const guiaFim = doutoBannersEnabled() && GUIA_FIM_PATHS.includes(page.path);
  const showDoutoCard = template !== 'institutional' && !GUIDE_DOUTO_PATHS.includes(page.path) && !guiaFim;
  const nextReview = page.lastReviewed ? addMonthsIso(page.lastReviewed, 6) : null;

  const centralRelated = getGuideRelated(page.path)
    .map((path) => getGuidePage(path))
    .filter(Boolean)
    .map((item) => ({
      to: item.path,
      label: item.breadcrumb,
      note: item.description
    }));

  const mergedRelated = [...related, ...centralRelated]
    .filter((item) => item.to !== page.path)
    .filter((item, index, list) => list.findIndex((candidate) => candidate.to === item.to) === index);

  return (
    <div className={`douto-layout${template === 'institutional' ? ' douto-layout--single' : ''}`}>
    <article className="public-article mx-auto max-w-[800px] px-5 py-9 md:px-10 md:py-14">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(buildPageJsonLd(page)) }}
      />

      <div className="mb-6 flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1">
        <nav aria-label="Trilha de navegação" className="text-sm text-[var(--text-muted)]">
          <ol className="flex flex-wrap gap-2">
            <li><Link to="/" className="hover:underline">Mini Mental</Link></li>
            <li aria-hidden="true">›</li>
            <li><Link to="/guias" className="hover:underline">Guias do MEEM</Link></li>
            <li aria-hidden="true">›</li>
            <li aria-current="page" className="font-semibold text-[var(--primary)]">{page.breadcrumb}</li>
          </ol>
        </nav>
        {template !== 'institutional' && <GuideCalcInvite />}
      </div>

      <h1 className="mb-5 leading-tight">{page.heading}</h1>
      <div className="mb-12 rounded-[8px] border border-[var(--line)] border-l-[3px] border-l-[var(--accent)] bg-[var(--surface)] px-5 py-4 md:px-6 md:py-5">
        <p className="text-[length:var(--fs-14)] font-semibold text-[var(--accent)]">Resposta curta</p>
        <p className="mt-1 text-[length:var(--fs-17)] leading-relaxed text-[var(--ink)] md:text-[length:var(--fs-17)]">{answer}</p>
      </div>

      {template !== 'institutional' && page.path !== '/mini-mental-pdf' && <GuiaBanner placement="guia-topo" className="mb-12" />}
      <div className="space-y-12">{children}</div>
      {guiaFim && <div className="mt-10 min-h-[8rem]"><DoutoBanner placement="guia-fim" variant="faixa" /></div>}
      {GUIDE_DOUTO_PATHS.includes(page.path) && !guiaFim && <GuideDoutoNextStep />}
      {showDoutoCard && <div className="douto-article-inline"><DoutoFeatureCard placement="article_inline" /></div>}

      {references && (
        <section aria-labelledby="referencias-pagina" className="mt-12 pt-8 border-t border-[var(--border-strong)]">
          <h2 id="referencias-pagina" className="mb-4">Referências desta página</h2>
          <ul className="space-y-3 text-sm text-[var(--text-muted)]">
            {references.map((ref, i) => (
              <li key={i} className="flex gap-3"><span className="text-[var(--accent)] font-bold">{i + 1}.</span>{ref}</li>
            ))}
          </ul>
        </section>
      )}

      <section aria-labelledby="continue-lendo" className="mt-10 pt-8 border-t border-[var(--border-strong)]">
        <div className="flex flex-wrap items-baseline justify-between gap-3 mb-5">
          <h2 id="continue-lendo">Continue no Mini Mental</h2>
          <Link to="/guias" className="inline-flex min-h-11 items-center text-sm text-[var(--primary)] underline">Todos os guias →</Link>
        </div>
        <ul className="space-y-3 text-sm text-[var(--text-muted)]">
          {mergedRelated.map((item) => (
            <li key={item.to}><Link to={item.to} className=" text-[var(--primary)] underline">{item.label}</Link>{' '}— {item.note}</li>
          ))}
        </ul>
      </section>

      <section aria-labelledby="aviso-medico" className="margin-note mt-10 border-[var(--cut)]/40 bg-[var(--cut-tint)] px-5 py-4">
        <h2 id="aviso-medico" className="mb-2 font-sans text-base font-bold text-[var(--warning-text)]">Aviso médico</h2>
        <p className="text-sm text-[var(--warning-text)] leading-relaxed">
          O desempenho pode ser influenciado por escolaridade, condições sensoriais,
          linguagem, estado emocional, delirium, dor, fadiga e outras condições clínicas.
          Este site possui finalidade educacional e não substitui avaliação médica profissional.
        </p>
      </section>

      <section aria-labelledby="autoria" className="mt-8 border-t border-[var(--border)] pt-6">
        <h2 id="autoria" className="mb-3 font-sans text-base font-semibold">Autoria e revisão</h2>
        <dl className="grid sm:grid-cols-2 gap-x-8 gap-y-2 text-sm text-[var(--text-muted)]">
          <div><dt className="inline font-semibold">Autor e revisor técnico: </dt><dd className="inline">Dr. Carlos Felipe</dd></div>
          <div><dt className="inline font-semibold">Registro: </dt><dd className="inline">CRM-MG 105.721</dd></div>
          <div><dt className="inline font-semibold">Contato editorial: </dt><dd className="inline"><ContactEmail className="hover:underline" /></dd></div>
          <div><dt className="inline font-semibold">Conflitos de interesse: </dt><dd className="inline">nenhum declarado</dd></div>
          <div><dt className="inline font-semibold">Publicado em: </dt><dd className="inline">{formatEditorialDate(page.publishedAt)}</dd></div>
          <div><dt className="inline font-semibold">Última revisão: </dt><dd className="inline">{formatEditorialDate(page.lastReviewed)}</dd></div>
          {nextReview && <div><dt className="inline font-semibold">Próxima revisão prevista: </dt><dd className="inline">{formatEditorialDate(nextReview)}</dd></div>}
        </dl>
        <p className="mt-4 text-sm text-[var(--text-muted)]">
          Esta página faz parte dos <strong>Guias do Mini Mental (MEEM)</strong> e aprofunda um tema da calculadora e do conteúdo editorial do site.
        </p>
      </section>
    </article>
    {template !== 'institutional' && (
      <div className={`${showDoutoCard ? 'douto-rail' : 'guide-rail'} no-print`}>
        <GuideToc />
        {showDoutoCard && <DoutoFeatureCard placement="article_sidebar" variant="sidebar" />}
      </div>
    )}
    </div>
  );
}
